import Component from "../../Component";
import { Seat, AvailableSeat } from "../types";
import BaseApi from "./BaseApi";
import formatter from "../utils/formatter";

// Define types for better type safety

/**
 * Seat API - Handles all seat-related operations
 * Extends BaseApi for consistent error handling and authentication
 * Follows Single Responsibility Principle
 */
export default class SeatApi extends BaseApi {
    constructor(component: Component) {
        super(component);
    }

    /**
     * Fetches available seats for a given time period
     * @param startDate Start date in ISO format
     * @param endDate End date in ISO format
     * @param locationId Optional location ID filter
     * @param floorId Optional floor ID filter
     * @returns Promise<AvailableSeat[]> Array of available seats
     */
    public async fetchFreeSpaces(
        startDate: string,
        endDate: string,
        locationId?: string,
        floorId?: string
    ): Promise<AvailableSeat[]> {
        // Ensure times are in German local format
        const localStartDate = this.ensureLocalTimeFormat(startDate);
        const localEndDate = this.ensureLocalTimeFormat(endDate);

        // Build query parameters
        const params = new URLSearchParams({
            start: localStartDate,
            end: localEndDate
        });

        if (locationId) {
            params.append("locationId", locationId);
        }

        if (floorId) {
            params.append("floorId", floorId);
        }

        const endpoint = `/seats/available?${params.toString()}`;
        return this.fetchWithAuth<AvailableSeat[]>(endpoint);
    }

    /**
     * Fetches available favorite seats for a given time period
     * @param startDate Start date in ISO format
     * @param endDate End date in ISO format
     * @param locationId Optional location ID filter
     * @param floorId Optional floor ID filter
     * @returns Promise<AvailableSeat[]> Array of available favorite seats
     */
    public async fetchAvailableFavoriteSeats(
        startDate: string,
        endDate: string,
        locationId?: string,
        floorId?: string
    ): Promise<AvailableSeat[]> {
        // Ensure times are in German local format
        const localStartDate = this.ensureLocalTimeFormat(startDate);
        const localEndDate = this.ensureLocalTimeFormat(endDate);

        // Build query parameters
        const params = new URLSearchParams({
            start: localStartDate,
            end: localEndDate
        });

        if (locationId) {
            params.append("locationId", locationId);
        }

        if (floorId) {
            params.append("floorId", floorId);
        }

        const endpoint = `/employee/favoriteseats/available?${params.toString()}`;
        return this.fetchWithAuth<AvailableSeat[]>(endpoint);
    }

    /**
     * Fetches available non-favorite seats for a given time period
     * @param startDate Start date in ISO format
     * @param endDate End date in ISO format
     * @param locationId Optional location ID filter
     * @param floorId Optional floor ID filter
     * @returns Promise<AvailableSeat[]> Array of available non-favorite seats
     */
    public async fetchAvailableNonFavoriteSeats(
        startDate: string,
        endDate: string,
        locationId?: string,
        floorId?: string
    ): Promise<AvailableSeat[]> {
        // Ensure times are in German local format
        const localStartDate = this.ensureLocalTimeFormat(startDate);
        const localEndDate = this.ensureLocalTimeFormat(endDate);

        // Build query parameters
        const params = new URLSearchParams({
            start: localStartDate,
            end: localEndDate
        });

        if (locationId) {
            params.append("locationId", locationId);
        }

        if (floorId) {
            params.append("floorId", floorId);
        }

        const endpoint = `/employee/nonfavoriteseats/available?${params.toString()}`;
        return this.fetchWithAuth<AvailableSeat[]>(endpoint);
    }

    public async fetchAll(): Promise<Seat[]> {
        const endpoint = "/seats";

        return this.fetchWithAuth<Seat[]>(endpoint);
    }

    public async fetchById(seatId: string): Promise<Seat> {
        const endpoint = `/seats/${seatId}`;
        return this.fetchWithAuth<Seat>(endpoint);
    }
    /**
     * Checks if a seat is available for a given time period
     * @param seatId Seat ID to check
     * @param startTime Start time in ISO format
     * @param endTime End time in ISO format
     * @returns Promise<{ available: boolean }> Availability status
     */
    public async checkSeatAvailability(
        seatId: string,
        startTime: string,
        endTime: string
    ): Promise<{ available: boolean }> {
        try {
            // Ensure times are in German local format
            const localStartTime = this.ensureLocalTimeFormat(startTime);
            const localEndTime = this.ensureLocalTimeFormat(endTime);

            // Extract the date and create full day range for the backend query
            const requestDate = new Date(localStartTime);
            const dayStart = new Date(requestDate);
            dayStart.setHours(0, 0, 0, 0);

            const dayEnd = new Date(requestDate);
            dayEnd.setHours(23, 59, 59, 999);

            // Query the backend for the entire day to get all bookings
            const params = new URLSearchParams({
                startDate: formatter.formatLocalDateTime(dayStart),
                endDate: formatter.formatLocalDateTime(dayEnd)
            });

            const availabilityData = await this.fetchWithAuth<any>(
                `/booking/timespan?${params.toString()}`
            );

            // Check if the specific seat has any bookings that conflict with our requested time
            if (availabilityData?.seats && Array.isArray(availabilityData.seats)) {
                const seatBookings = availabilityData.seats.find(
                    (bookedSeat: any) => bookedSeat.seatId === seatId
                );

                if (seatBookings?.bookedTimes && Array.isArray(seatBookings.bookedTimes)) {
                    // Check if any of the booked times conflict with our requested time range
                    const hasConflict = seatBookings.bookedTimes.some((bookedTime: any) => {
                        const bookingStart = new Date(bookedTime.startTime);
                        const bookingEnd = new Date(bookedTime.endTime);
                        const requestStart = new Date(localStartTime);
                        const requestEnd = new Date(localEndTime);

                        // Check for time overlap: booking conflicts if it starts before our end time AND ends after our start time
                        return bookingStart < requestEnd && bookingEnd > requestStart;
                    });

                    // Seat is available if there are NO conflicts
                    return { available: !hasConflict };
                }
            }

            // If no booked seats data or no bookings for this seat, seat is available
            return { available: true };
        } catch (error) {
            console.error("Error checking seat availability:", error);
            // In case of error, assume seat is not available for safety
            return { available: false };
        }
    }

    /**
     * Ensures datetime strings are in German local time format
     * If a Date object is passed, converts it to local time string
     * If already a string, assumes it's in correct local format and passes through
     * @param dateInput - Date object or datetime string
     * @returns Local datetime string in format YYYY-MM-DDTHH:mm:ss
     */
    private ensureLocalTimeFormat(dateInput: string | Date): string {
        if (dateInput instanceof Date) {
            // Convert Date object to local time string
            return formatter.formatLocalDateTime(dateInput);
        } else if (typeof dateInput === "string") {
            // convert it to local time, otherwise pass through
            if (
                dateInput.endsWith("Z") ||
                dateInput.includes("+") ||
                /T\d{2}:\d{2}:\d{2}\.\d{3}$/.exec(dateInput)
            ) {
                // If it looks like a UTC timestamp, convert to local time
                const dateObj = new Date(dateInput);
                return formatter.formatLocalDateTime(dateObj);
            } else {
                // Assume it's already in local format
                return dateInput;
            }
        }

        // Fallback: treat as string
        return String(dateInput);
    }
}
